<script setup>
  import { computed, ref } from 'vue';

  import CredereButton from '@/components/Core/Button.vue';

  const props = defineProps({
    color: {
      type: String,
      required: false,
      default: 'danger',
      validator(value) {
        return ['danger', 'success', 'information'].includes(value);
      },
    },
    marginless: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Optional `v-model:open`: unbound, the alert owns whether it is showing; bound,
    // the parent does.
    open: {
      type: Boolean,
      required: false,
      default: null,
    },
    // The alert that takes the page, rather than a strip inside it.
    big: {
      type: Boolean,
      required: false,
      default: false,
    },
    // The alert that does not interrupt: a light background instead of the dark surface.
    light: {
      type: Boolean,
      required: false,
      default: false,
    },
    // On which side the alert needs air. `marginless` is a different axis and wins
    // over this one.
    position: {
      type: String,
      required: false,
      default: 'top',
      validator(value) {
        return ['top', 'middle', 'bottom'].includes(value);
      },
    },
    /*
     * The action's label; the alert renders the button itself, because the surface is
     * what decides the button's color and size, and the caller cannot know it.
     */
    button: {
      type: String,
      required: false,
      default: null,
    },

    // The action's destination, when it is a place: with `href` the control becomes a real
    // anchor, and cmd+click and middle click start to exist.
    buttonHref: {
      type: String,
      required: false,
      default: null,
    },

    // `_blank` for a place of another domain, which opens in a tab of its own.
    buttonTarget: {
      type: String,
      required: false,
      default: null,
    },

    buttonLoading: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Adds the close control. Closing lasts this instance's lifetime, so a screen that
    // swaps the message in place has to `:key` the alert on it.
    dismissible: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const emit = defineEmits(['click', 'close', 'update:open']);

  // The action's step follows the alert's; computed because `big` can change after mount.
  const buttonSize = computed(() => (props.big ? 'medium' : 'small'));

  const buttonRel = computed(() => (props.buttonTarget === '_blank' ? 'noopener' : null));

  // Two tracks: the text empties the box early, then the box closes, so whatever sits
  // below it rises instead of snapping.
  const FADE_DURATION = 120;
  const COLLAPSE_DURATION = 220;
  const COLLAPSE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  // The uncontrolled half; `visible` reads the prop when the parent bound one.
  const uncontrolled = ref(true);
  const visible = computed(() => (props.open === null ? uncontrolled.value : props.open));

  function click() {
    emit('click');
  }

  // A computed and not a const: the props can change after mount, and a value read once
  // at setup would freeze the class list.
  const classes = computed(() => ({
    'credere-alert': true,
    [`color--${props.color}`]: true,
    [`position--${props.position}`]: true,
    'variant--marginless': props.marginless,
    'variant--big': props.big,
    'variant--light': props.light,
  }));

  function close() {
    uncontrolled.value = false;

    emit('update:open', false);
    emit('close');
  }

  // Driven from JS because the height has to be measured before it can be animated:
  // `height: auto` has nothing to interpolate from.
  function leave(element, done) {
    // Less movement means none of it, not a shorter version of it.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();

      return;
    }

    const { height } = element.getBoundingClientRect();
    const { marginBottom, paddingTop, paddingBottom } = getComputedStyle(element);

    /*
     * The vertical padding gets its own tracks: under `box-sizing: border-box` a used
     * height below padding + border is clamped to it, so `height` alone stalls the collapse.
     */
    element.style.overflow = 'hidden';

    element.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: FADE_DURATION, easing: 'linear', fill: 'forwards' },
    );

    const collapse = element.animate(
      [
        {
          height: `${height}px`, marginBottom, paddingTop, paddingBottom,
        },
        {
          height: '0px', marginBottom: '0px', paddingTop: '0px', paddingBottom: '0px',
        },
      ],
      { duration: COLLAPSE_DURATION, easing: COLLAPSE_EASING, fill: 'forwards' },
    );

    // `finished` rejects when the animation is cancelled, and Vue still needs its
    // callback or the element never leaves the DOM.
    collapse.finished.then(done, done);
  }
</script>

<template>
  <!--
    `:css="false"`: only `done()` from the hook below ends the leave. Entering has no
    hook - the alert appearing is immediate.
  -->
  <transition
    :css="false"
    @leave="leave"
  >
    <section
      v-if="visible"
      :class="classes"
    >
      <!-- Two axes, so two elements: the outer row centers the text block against the
           action, and the inner one puts the symbol on the text's first line. -->
      <div class="credere-alert-content">
        <span class="credere-alert-icon">!</span>

        <div class="credere-alert-paragraph">
          <slot />
        </div>
      </div>

      <!-- `type="button"` is load-bearing: inside a `<form>`, a control with no type
           submits the form the alert is sitting on top of. -->
      <div
        v-if="button"
        class="credere-alert-action"
      >
        <credere-button
          class="credere-alert-action-button"
          color="highlight"
          :href="buttonHref"
          :target="buttonTarget"
          :rel="buttonRel"
          :size="buttonSize"
          type="button"
          :loading="buttonLoading"
          @click="click"
        >
          {{ button }}
        </credere-button>
      </div>

      <!--
        `type="button"` is load-bearing: inside a `<form>`, a bare `<button>` defaults to
        `submit`, so closing the error would submit the form that produced it.
      -->
      <button
        v-if="dismissible"
        class="credere-alert-close"
        type="button"
        aria-label="Fechar aviso"
        @click="close"
      >
        <font-awesome-icon icon="times" />
      </button>
    </section>
  </transition>
</template>

<style lang="scss">
  @import '@css/mixins/mobile.scss';

  .credere-alert {
    --background-color: var(--cordel-color-constant-dark);
    --border-color: var(--cordel-color-feedback-danger);
    --border-style: solid;
    --border-width: 0 0 0 var(--cordel-spacing-xxxs);
    --border-radius: var(--cordel-border-radius-small);
    --color: var(--cordel-color-constant-light);
    --font-size: var(--cordel-font-size-m);
    // This `--gap` separates the text block, the action and the `×`.
    --gap: var(--cordel-spacing-xxxs);
    --line-height: var(--cordel-line-height-l);
    // The two edges separately, so a rule can move one without erasing the other.
    // Namespaced because a bare `--margin-top` would inherit into every descendant.
    --alert-margin-top: 0;
    --alert-margin-bottom: var(--cordel-spacing-blaster);
    --margin: var(--alert-margin-top) 0 var(--alert-margin-bottom) 0;
    --padding: var(--cordel-spacing-xxs);

    // The text block, the action and the `×` center against each other; the symbol stays on
    // the text's first line, inside `-content`.
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    @include mobile-font-size;

    /*
     * Narrow, the row breaks in two and the action drops to a full-width line. Gated on
     * the container alone: type follows the device, layout follows the box it has to fit.
     */
    @container (max-width: 480px) {
      // Row gap first, column gap second: the row gap is the space the two lines need
      // between them, while the icon and the sentence keep their tighter spacing.
      --gap: var(--cordel-spacing-xxl) var(--cordel-spacing-xxxs);

      flex-wrap: wrap;
    }

    // `position--top` has no block: it is the pair declared above.
    &.position--middle {
      --alert-margin-top: var(--cordel-spacing-blaster);
    }

    &.position--bottom {
      --alert-margin-top: var(--cordel-spacing-blaster);
      --alert-margin-bottom: 0;
    }

    // Two alerts in a row are one block: touches the bottom edge only, and comes after
    // the positions because they weigh the same and source order is what decides.
    &:has(+ .credere-alert) {
      --alert-margin-bottom: var(--cordel-spacing-s);
    }

    // "No margin at all" redeclares the shorthand, so it wins over the edge variables
    // by construction, not by source order.
    &.variant--marginless {
      --margin: 0;
    }

    // The alert that takes the page: more air and a thicker color bar, with the air cut
    // down in a narrow container, where it would eat the sentence.
    &.variant--big {
      --border-width: 0 0 0 var(--cordel-spacing-xs);
      // Row and column apart: the column separates the text block from the action, and the
      // row only shows up once the line wraps.
      --gap: var(--cordel-spacing-xxxs) var(--cordel-spacing-xl);
      --padding: var(--cordel-spacing-xxxl) var(--cordel-spacing-blaster);

      @container (max-width: 480px) {
        --padding: var(--cordel-spacing-s);
      }
    }

    // Light: the color's own wash behind the text, so the box still says what kind of notice it is.
    &.variant--light {
      --background-color: var(--cordel-color-feedback-danger-soft);
      --color: var(--cordel-color-constant-dark);
    }

    &.color--success {
      --border-color: var(--cordel-color-feedback-success);

      &.variant--light {
        --background-color: var(--cordel-color-highlight-light);
        --border-color: var(--cordel-color-highlight-dark);
      }
    }

    &.color--information {
      --border-color: var(--cordel-color-feedback-information);

      &.variant--light {
        --background-color: var(--cordel-color-feedback-information-soft);
      }
    }

    b {
      --font-weight: var(--cordel-font-weight-bold);
    }
  }

  /*
   * The symbol on the text's first line: the `flex-start` here counts against the text's
   * height, not the whole row's, where the action is what rules.
   */
  .credere-alert-content {
    --gap: var(--cordel-spacing-xxxs);

    align-items: flex-start;
    display: flex;
    flex: 1;
    flex-direction: row;
    justify-content: flex-start;
    min-width: 0;

    .variant--big & {
      --gap: var(--cordel-spacing-xl);
    }
  }

  .credere-alert-icon {
    --background-color: var(--cordel-color-feedback-danger);
    --border-radius: var(--cordel-border-radius-circular);
    // Light on the red and the blue disc, dark on the green: the pair that reads, each time.
    --color: var(--cordel-color-constant-light);
    --font-size: var(--cordel-font-size-s);
    --font-weight: var(--cordel-font-weight-bold);
    --margin: 0.25rem 0 0;

    flex-shrink: 0;
    height: 1rem;
    line-height: 1rem;
    text-align: center;
    width: 1rem;

    // In `big` the disc is taller than the text's first line, so the nudge that pushed it
    // against that line goes away.
    .variant--big & {
      --font-size: var(--cordel-font-size-xl);
      --margin: 0;

      height: 1.6rem;
      line-height: 1.6rem;
      width: 1.6rem;
    }

    .color--success & {
      --background-color: var(--cordel-color-feedback-success);
      --color: var(--cordel-color-constant-dark);
    }

    .color--information & {
      --background-color: var(--cordel-color-feedback-information);
    }
  }

  .credere-alert-paragraph {
    display: block;
    flex: 1;

    /*
     * A flex item refuses to shrink below its longest word, and that refusal pushes the
     * close control out of the alert. Declared here and on the grouper: one per flex item.
     */
    min-width: 0;
  }

  // `flex-shrink: 0` keeps the button at its own width while the text takes the rest;
  // `align-self` centers it against a sentence that may wrap to several lines.
  .credere-alert-action {
    align-items: center;
    align-self: center;
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    justify-content: flex-end;

    @container (max-width: 480px) {
      /*
       * `order` is what puts the close back on the first line. The DOM order is the tab
       * order and does not move, so stacked, focus reaches the action before the ×.
       */
      flex-basis: 100%;
      order: 1;
    }
  }

  // The wrapper claiming the line is not enough: the button is a flex item, and would
  // otherwise sit at its label's width.
  .credere-alert-action-button {
    @container (max-width: 480px) {
      width: 100%;
    }
  }

  .credere-alert-close {
    --border-radius: var(--cordel-border-radius-small);
    --color: var(--cordel-color-constant-light);
    --font-size: var(--cordel-font-size-l);
    --line-height: var(--cordel-line-height-s);
    --padding: var(--cordel-spacing-xxs);

    /*
     * The padding makes the 44px target, and the equal negative margin hangs it back over
     * the alert's own padding, so the glyph lands on the edge instead of growing the row.
     */
    --margin: calc(var(--cordel-spacing-xxs) * -1)
              calc(var(--cordel-spacing-xxs) * -1)
              calc(var(--cordel-spacing-xxs) * -1)
              auto;

    align-items: center;
    appearance: none;

    // The glyph is narrower than it is tall, so a padded box alone would miss the target
    // floor on one axis only.
    aspect-ratio: 1 / 1;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    justify-content: center;
    min-width: calc(var(--cordel-spacing-xxs) * 2 + var(--font-size) * var(--line-height));

    // The UA ring paints over the token ring below, and in the browser's own blue.
    outline: none;
    transition: box-shadow .1s, color .1s, transform .1s;

    // The glyph reads the same on all three colors: `color` moves the left border and
    // the icon chip, never the surface.
    &:hover {
      --color: var(--cordel-color-highlight-primary);
    }

    /*
     * Ring on `:focus`, then taken back for the pointer case: in this order, a browser
     * without `:focus-visible` drops the second rule and keeps the indicator.
     */
    &:focus {
      --box-shadow: 0 0 0 var(--cordel-border-width-heavy) var(--cordel-color-feedback-information);
    }

    &:focus:not(:focus-visible) {
      --box-shadow: none;
    }

    &:active {
      transform: translateY(2px);
    }
  }
</style>
